import type { SiteData, BracketData } from "../types";
import { bracketScenario8 } from "./brackets8";
import { bracketScenario16 } from "./brackets16";
import { bracketScenario32 } from "./brackets32";
import { bracketScenario64 } from "./brackets64";

export interface PickWinnerResult {
  siteData: SiteData;
  brackets: BracketData;
}

function finalRoundDoubleElimination(
  siteData: SiteData,
  winner: number,
  loser: number,
): SiteData {
  const updated = structuredClone(siteData);
  const { gameResults } = updated.bracketsModeSettings;
  gameResults.push(loser);
  gameResults.push(winner);
  for (let x = 0; x < gameResults.length; x += 1) {
    updated.bracketsModeSettings.gameResultSeeds.push([gameResults[x]!]);
  }
  updated.bracketsModeSettings.current2Seeds = [winner];
  updated.gameFinished = true;
  return updated;
}

function setNextWinnerSeed(siteData: SiteData, winner: number): SiteData {
  const nextSeedList =
    siteData.bracketsModeSettings.gameSeedSetup.nextSeedList;
  const { nextSeeds } = siteData.bracketsModeSettings.gameSeedSetup;

  siteData.bracketsModeSettings.gameSeedSetup.nextSeedList.push(winner);

  let seedCount = 1;
  for (let x = 0; x < nextSeedList.length; x += 2) {
    const song1 = nextSeedList[x];
    const song2 = nextSeedList[x + 1] ? nextSeedList[x + 1] : undefined;

    if (!nextSeeds["seed" + seedCount]) {
      nextSeeds["seed" + seedCount] = [];
      const songList: number[] = [];
      if (song1 !== undefined) songList.push(song1);
      if (song2 !== undefined) songList.push(song2);
      nextSeeds["seed" + seedCount] = songList;
    } else if (nextSeeds["seed" + seedCount]!.length < 2) {
      const songList = nextSeeds["seed" + seedCount]!;
      if (song2 !== undefined) {
        songList.push(song2);
        nextSeeds["seed" + seedCount] = songList;
      }
    }
    seedCount += 1;
  }
  return siteData;
}

function isFinalRound(siteData: SiteData): boolean {
  const { bracketSize } = siteData;
  const { currentRound } = siteData.bracketsModeSettings;
  if (bracketSize === 8 && currentRound === 15) return true;
  if (bracketSize === 8 && currentRound === 7) return true;
  if (bracketSize === 16 && currentRound === 31) return true;
  return false;
}

export function pickWinner(
  inputSiteData: SiteData,
  inputBrackets: BracketData,
  winner: number,
): PickWinnerResult {
  const siteData = structuredClone(inputSiteData);
  const brackets = structuredClone(inputBrackets);
  const { current2Seeds, gameSeedSetup, currentSeed } =
    siteData.bracketsModeSettings;
  const { gameResults } = siteData.bracketsModeSettings;
  const loserIdx = current2Seeds.indexOf(winner) === 0 ? 1 : 0;
  const loser = current2Seeds[loserIdx]!;

  // Double elimination bracket scenarios
  if (siteData.bracketSize === 8 && siteData.gameType === "DoubleE") {
    const result = bracketScenario8(siteData, brackets, winner, loser);
    if (result.isFinal) {
      return {
        siteData: finalRoundDoubleElimination(result.siteData, winner, loser),
        brackets: result.brackets,
      };
    }
    return { siteData: result.siteData, brackets: result.brackets };
  }
  if (siteData.bracketSize === 16 && siteData.gameType === "DoubleE") {
    const result = bracketScenario16(siteData, brackets, winner, loser);
    if (result.isFinal) {
      return {
        siteData: finalRoundDoubleElimination(result.siteData, winner, loser),
        brackets: result.brackets,
      };
    }
    return { siteData: result.siteData, brackets: result.brackets };
  }
  if (siteData.bracketSize === 32 && siteData.gameType === "DoubleE") {
    const result = bracketScenario32(siteData, brackets, winner, loser);
    if (result.isFinal) {
      return {
        siteData: finalRoundDoubleElimination(result.siteData, winner, loser),
        brackets: result.brackets,
      };
    }
    return { siteData: result.siteData, brackets: result.brackets };
  }
  if (siteData.bracketSize === 64 && siteData.gameType === "DoubleE") {
    const result = bracketScenario64(siteData, brackets, winner, loser);
    if (result.isFinal) {
      return {
        siteData: finalRoundDoubleElimination(result.siteData, winner, loser),
        brackets: result.brackets,
      };
    }
    return { siteData: result.siteData, brackets: result.brackets };
  }

  // Single elimination or final-round check
  if (isFinalRound(siteData)) {
    return {
      siteData: finalRoundDoubleElimination(siteData, winner, loser),
      brackets,
    };
  }

  // Single elimination: advance winner
  if (siteData.bracketType === "Winners") {
    setNextWinnerSeed(siteData, winner);
  }
  delete gameSeedSetup.activeSeeds["seed" + currentSeed];

  // Record loser for single elimination
  if (siteData.gameType === "SingleE") {
    gameResults.push(loser);
  }

  // Check if we need to advance to next round
  if (!gameSeedSetup.activeSeeds["seed" + (currentSeed + 1)]) {
    if (siteData.gameType === "SingleE") {
      gameSeedSetup.activeSeeds = gameSeedSetup.nextSeeds;
      gameSeedSetup.nextSeeds = {};
      gameSeedSetup.nextSeedList = [];
      siteData.bracketsModeSettings.currentSeed = 1;
      siteData.bracketsModeSettings.current2Seeds =
        gameSeedSetup.activeSeeds["seed1"]!;
      siteData.bracketsModeSettings.gameResultSeeds.push(gameResults.slice());
      siteData.bracketsModeSettings.gameResults = [];
    }
  } else {
    siteData.bracketsModeSettings.currentSeed += 1;
    siteData.bracketsModeSettings.current2Seeds =
      gameSeedSetup.activeSeeds[
        "seed" + siteData.bracketsModeSettings.currentSeed
      ]!;
  }

  // Single elimination round advancement
  if (siteData.gameType === "SingleE") {
    siteData.bracketsModeSettings.currentRound += 1;
    if (
      siteData.bracketsModeSettings.currentRound ===
      siteData.bracketSize - 1
    ) {
      siteData.bracketsModeSettings.gameResultSeeds.push([winner]);
      siteData.bracketsModeSettings.current2Seeds = [];
      siteData.gameFinished = true;
    }
  }

  return { siteData, brackets };
}

export function getMaxRounds(siteData: SiteData): number {
  let maxRounds = siteData.bracketSize;
  if (siteData.gameType === "DoubleE") {
    maxRounds = maxRounds * 2;
  }
  return maxRounds;
}
